import { useCallback, useMemo, useState } from 'react';
import { getCellAt, getHeaderRowCount } from '@/lib/table-utils';
import {
    expandSelectionToMerged,
    getSelectionRect,
    getSelectionRectGlobal,
    normalizeSelection,
} from './TableSelection';
import type { CellPosition, SelectionRange, TableSection } from './TableSelection';
import type { TableBlock } from '@/types/report-payload';

type UseTableSelectionProps = {
    block: TableBlock;
    getOriginPosition: (cellId: string) => CellPosition | null;
};

type UseTableSelectionResult = {
    selection: SelectionRange | null;
    setSelection: (selection: SelectionRange | null) => void;
    applySelection: (selection: SelectionRange) => void;
    selectionRect: {
        rowStart: number;
        rowEnd: number;
        colStart: number;
        colEnd: number;
    } | null;
    hasMultiCellSelection: boolean;
    selectionHasMerge: () => boolean;
};

export const useTableSelection = ({
    block,
    getOriginPosition,
}: UseTableSelectionProps): UseTableSelectionResult => {
    const [selection, setSelection] = useState<SelectionRange | null>(null);
    const headerRowCount = getHeaderRowCount(block);

    const applySelection = useCallback(
        (nextSelection: SelectionRange) => {
            const expanded = expandSelectionToMerged(
                nextSelection,
                (section: TableSection, rowIndex: number, colIndex: number) =>
                    getCellAt(block, section, rowIndex, colIndex),
                getOriginPosition,
                headerRowCount,
            );
            setSelection(expanded);
        },
        [block, getOriginPosition, headerRowCount],
    );

    const selectionRect = useMemo(() => {
        if (!selection) return null;
        return getSelectionRect(normalizeSelection(selection, headerRowCount), headerRowCount);
    }, [selection, headerRowCount]);

    const hasMultiCellSelection = useMemo(() => {
        if (!selection) return false;
        // Use global-row space so a header + body-row-0 single-column selection
        // is correctly detected as multi-cell (their section-local rows are both 0).
        const globalRect = getSelectionRectGlobal(selection, headerRowCount);
        return (
            globalRect.rowStart !== globalRect.rowEnd || globalRect.colStart !== globalRect.colEnd
        );
    }, [selection, headerRowCount]);

    const selectionHasMerge = useCallback(() => {
        if (!selectionRect || !selection) return false;
        for (let r = selectionRect.rowStart; r <= selectionRect.rowEnd; r += 1) {
            for (let c = selectionRect.colStart; c <= selectionRect.colEnd; c += 1) {
                const cell = getCellAt(block, selection.start.section, r, c);
                if (!cell) continue;
                if (cell.mergedInto) return true;
                if ((cell.rowSpan || 1) > 1 || (cell.colSpan || 1) > 1) return true;
            }
        }
        return false;
    }, [block, selection, selectionRect]);

    return {
        selection,
        setSelection,
        applySelection,
        selectionRect,
        hasMultiCellSelection,
        selectionHasMerge,
    };
};
